import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import avatarService from '../services/AgentAvatarService';
import { HoverCard, HoverCardProvider } from './HoverCard';
import { useImageCache } from '../hooks/useImageCache';
import { useAgentIdentity } from '../hooks/useAgentIdentity';
import imageCacheService from '../services/ImageCacheService';
import './AgentChat.css';

const AgentAvatar = ({ agent, getAgentInitials, style, className }) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });
  const avatarRef = useRef(null);
  const { cachedUrl } = useImageCache(avatarUrl);
  const lastFetchedRef = useRef({ id: null, name: null, prospectKey: null });

  // Default values for when agent is undefined
  const agentId = agent?.id || (typeof agent === 'object' ? agent?.name : agent) || 'system';
  const agentName = (typeof agent === 'object' ? agent?.name : agent) || 'system';
  const { humanName, initials: hookInitials } = useAgentIdentity(agentName);
  const displayInitials = hookInitials || getAgentInitials(agentName);

  const isDigitalTwin = agentName?.toLowerCase().includes('digitaltwin');
  const prospectNameForDisplay = (agent?._prospectName || window.currentProspectName || '').trim();
  const fallbackDisplayName = humanName || agentName.replace(/_/g, ' ');
  const displayNameForUI = isDigitalTwin
    ? (prospectNameForDisplay
      ? `${prospectNameForDisplay}'s Digital Twin`
      : agent?.displayName || fallbackDisplayName)
    : (agent?.displayName || fallbackDisplayName);

  // Prepare structured agent object for HoverCard
  const structuredAgent = {
    id: agentId,
    name: displayNameForUI,
    description: typeof agent === 'object' ? agent?.description : '',
    role: typeof agent === 'object' ? agent?.role : ''
  };

  // Immediately clear avatar when prospect changes for DigitalTwinAgent
  useEffect(() => {
    if (!isDigitalTwin) return;

    const currentNameRaw = agent?._prospectName || agent?.displayName || (typeof window !== 'undefined' ? window.currentProspectName : '');
    const currentEmailRaw = agent?._prospectEmail || (typeof window !== 'undefined' ? window.currentProspectEmail : '');
    const currentImageRaw = typeof window !== 'undefined'
      ? (typeof window.currentProspectImage === 'string'
        ? window.currentProspectImage.trim()
        : window.currentProspectImage || '')
      : '';

    const currentName = (currentNameRaw || '').trim();
    const currentEmail = (currentEmailRaw || '').trim();
    const prospectKey = `${currentName}::${currentEmail || 'no-email'}::${currentImageRaw || 'no-image'}`;

    if (!currentName && !currentEmail && !currentImageRaw) {
      return;
    }

    if (lastFetchedRef.current.prospectKey !== prospectKey) {
      console.log('[DigitalTwin] Prospect context changed, clearing cached avatar', {
        previous: lastFetchedRef.current.prospectKey,
        next: prospectKey,
        name: currentName,
        email: currentEmail
      });
      setAvatarUrl(null);
      setLoadError(false);
      lastFetchedRef.current.prospectKey = prospectKey;
    }
  }, [isDigitalTwin, agent?._prospectUpdate, agent?._prospectName, agent?._prospectEmail, agent?.displayName]);

  useEffect(() => {
    let cancelled = false;

    const fetchAvatar = async () => {
      try {
        // For DigitalTwinAgent, include prospect data in cache check
        const isDigitalTwin = agentName?.toLowerCase().includes('digitaltwin');
        const windowProspect = typeof window !== 'undefined' ? {
          name: (window.currentProspectName || '').trim(),
          email: (window.currentProspectEmail || '').trim(),
          image: typeof window.currentProspectImage === 'string'
            ? window.currentProspectImage.trim()
            : (window.currentProspectImage || '')
        } : { name: '', email: '', image: '' };

        const prospectKey = isDigitalTwin
          ? `${(agent?._prospectName || windowProspect.name || '').trim()}::${(agent?._prospectEmail || windowProspect.email || '').trim() || 'no-email'}::${(windowProspect.image || '').trim() || 'no-image'}`
          : null;

        // Skip refetch if same agent and we already have a URL (unless prospect changed for DigitalTwin)
        if (!isDigitalTwin &&
          lastFetchedRef.current.id === agentId &&
          lastFetchedRef.current.name === agentName &&
          avatarUrl) {
          setLoadError(false);
          return;
        }

        // For DigitalTwin, also check if prospect hasn't changed
        if (isDigitalTwin &&
          lastFetchedRef.current.prospectKey === prospectKey &&
          avatarUrl) {
          setLoadError(false);
          return;
        }

        setIsLoading(true);
        setLoadError(false);

        console.log('Fetching avatar for agent:', {
          agentId,
          agentName,
          displayInitials,
          rawAgent: agent
        });

        const url = await avatarService.getAvatarUrl(agentId, agentName);

        if (cancelled) return;

        if (!url) {
          console.warn('No avatar URL returned for agent:', agentName);
          setLoadError(true);
          lastFetchedRef.current = { id: agentId, name: agentName, prospectKey };
        } else {
          // Preload the image immediately
          const img = new Image();
          img.onload = () => {
            if (cancelled) return;
            setAvatarUrl(url);
            setLoadError(false);
            lastFetchedRef.current = { id: agentId, name: agentName, prospectKey };
          };
          img.onerror = () => {
            if (cancelled) return;
            console.error('Failed to load image:', url);
            setLoadError(true);
            setAvatarUrl(null);
            lastFetchedRef.current = { id: agentId, name: agentName, prospectKey };
          };
          img.src = url;
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching avatar:', error);
        setLoadError(true);
        setAvatarUrl(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (agentId && agentName) {
      fetchAvatar();
    }

    return () => {
      cancelled = true;
    };
  }, [agentId, agentName, agent?._prospectUpdate, agent?._prospectName, agent?._prospectEmail, isDigitalTwin]);

  useEffect(() => {
    if (avatarUrl) {
      // Preload the image for HoverCard
      imageCacheService.preloadImage(avatarUrl);
    }
  }, [avatarUrl]);

  const handleMouseEnter = () => {
    requestAnimationFrame(() => {
      if (avatarRef.current) {
        const rect = avatarRef.current.getBoundingClientRect();
        const CARD_MIN_WIDTH = 360; // Matches HoverCard.css min-width
        const PADDING = 16; // viewport padding
        const desiredLeft = rect.left + (rect.width / 2) - (CARD_MIN_WIDTH / 2);
        const maxLeft = Math.max(0, window.innerWidth - CARD_MIN_WIDTH - PADDING);
        const clampedLeft = Math.max(PADDING, Math.min(maxLeft, desiredLeft));
        setHoverPosition({
          top: rect.bottom + 8,
          left: clampedLeft,
        });
        setIsHovered(true);
      }
    });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <HoverCardProvider>
      <div
        className={`agent-avatar-container ${className || ''}`}
        ref={avatarRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`agent-avatar ${isLoading ? 'loading' : ''} ${loadError ? 'error' : ''}`}
          style={{
            ...style,
            position: 'relative'
          }}
        >
          {isLoading ? (
            <div className="loading-spinner" />
          ) : !loadError && cachedUrl && cachedUrl.trim() !== '' ? (
            <img
              src={cachedUrl}
              alt={`${agentName} avatar`}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                backgroundColor: '#8B6914'  // Gold background while loading
              }}
              onError={() => {
                setLoadError(true);
                setAvatarUrl(null);  // Clear URL to force initials display
              }}
            />
          ) : (
            <span style={{
              fontSize: style?.fontSize || 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: '#8B6914',
              color: 'white',
              borderRadius: '50%',
              fontWeight: 'bold'
            }}>
              {isDigitalTwin
                ? (() => {
                  const name = prospectNameForDisplay || window.currentProspectName;
                  if (name && name.trim()) {
                    const parts = name.trim().split(/\s+/);
                    if (parts.length >= 2) {
                      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                    } else if (parts.length === 1) {
                      return parts[0].slice(0, 2).toUpperCase();
                    }
                  }
                  return 'DT';
                })()
                : displayInitials}
            </span>
          )}
        </motion.div>
        <div className="agent-name">{displayNameForUI}</div>
        {isHovered && (
          <HoverCard
            agent={structuredAgent}
            isVisible={isHovered}
            position={hoverPosition}
            avatarUrl={cachedUrl}
          />
        )}
      </div>
    </HoverCardProvider>
  );
};

AgentAvatar.propTypes = {
  agent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string
    })
  ]),
  getAgentInitials: PropTypes.func.isRequired,
  style: PropTypes.object,
  className: PropTypes.string
};

export default AgentAvatar;